import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Appointment, AppointmentKind } from '../api/types'
import { Icon } from '../components/Icon'
import { DateField } from '../components/DateField'
import { PeoplePicker } from '../components/PeoplePicker'
import { Modal } from '../components/Modal'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { useAsync } from '../lib/useAsync'
import { useLiveRefresh } from '../lib/useLiveRefresh'
import { tint } from '../lib/tint'
import { googleComposeUrl, openExternal } from '../lib/google'

type View = 'week' | 'month'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// The hours the grid always shows. A day with nothing before 8am shouldn't make
// you scroll past six empty rows; anything earlier or later widens the range
// rather than being hidden (see `hourRange`).
const DEFAULT_FIRST_HOUR = 7
const DEFAULT_LAST_HOUR = 20
const ROW_PX = 44

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
const startOfWeek = (d: Date) => addDays(d, -d.getDay())           // weeks run Sun–Sat
const startOfMonthGrid = (d: Date) => startOfWeek(new Date(d.getFullYear(), d.getMonth(), 1))
const sameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime()

const hhmm = (d: Date) =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined })

/** Events that overlap in time, laid out side by side instead of on top of each
 *  other. Each gets a column index and the width of the busiest cluster it is
 *  part of — the standard day-view treatment, kept deliberately simple: one pass
 *  left to right, a new cluster whenever nothing is still running. */
function layout(events: { appt: Appointment; from: number; to: number }[]) {
  const sorted = [...events].sort((a, b) => a.from - b.from || b.to - a.to)
  const placed: { appt: Appointment; from: number; to: number; col: number; of: number }[] = []
  let cluster: typeof placed = []
  let clusterEnd = -1

  const closeCluster = () => {
    const width = cluster.reduce((m, e) => Math.max(m, e.col + 1), 0)
    cluster.forEach((e) => { e.of = width })
    cluster = []
  }

  for (const e of sorted) {
    if (e.from >= clusterEnd && cluster.length) closeCluster()
    const taken = new Set(cluster.filter((c) => c.to > e.from).map((c) => c.col))
    let col = 0
    while (taken.has(col)) col += 1
    const entry = { ...e, col, of: 1 }
    cluster.push(entry)
    placed.push(entry)
    clusterEnd = Math.max(clusterEnd, e.to)
  }
  closeCluster()
  return placed
}

export function Calendar() {
  const [view, setView] = useState<View>('week')
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))
  const [booking, setBooking] = useState(false)
  const navigate = useNavigate()

  const [from, to] = useMemo(() => {
    if (view === 'week') {
      const s = startOfWeek(anchor)
      return [s, addDays(s, 7)]
    }
    const s = startOfMonthGrid(anchor)
    return [s, addDays(s, 42)]
  }, [view, anchor])

  const { data, error, loading, reload } = useAsync(
    () => api.appointments({ start: from.toISOString(), end: to.toISOString() }),
    [from.getTime(), to.getTime()],
  )

  // Opening the calendar is the moment an officer expects to see what they just
  // put in Google, so ask Google then rather than waiting for the next poll.
  useEffect(() => {
    let live = true
    api.pullCalendar()
      .then((r) => { if (live && (r.applied || r.pruned)) reload() })
      .catch(() => {})
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // And keep looking, for changes made elsewhere while this stays open.
  useLiveRefresh(reload)

  const appts = data?.appointments ?? []
  const timed = appts.filter((a) => !a.all_day)
  const allDay = appts.filter((a) => a.all_day)

  // Widen the grid only as far as the week's own events require.
  const [firstHour, lastHour] = useMemo(() => {
    let lo = DEFAULT_FIRST_HOUR
    let hi = DEFAULT_LAST_HOUR
    for (const a of timed) {
      lo = Math.min(lo, new Date(a.starts_at).getHours())
      hi = Math.max(hi, new Date(a.ends_at).getHours() + 1)
    }
    return [Math.max(0, lo), Math.min(24, hi)]
  }, [timed])

  const step = view === 'week' ? 7 : 0
  const go = (dir: -1 | 1) =>
    setAnchor((d) => (view === 'week'
      ? addDays(d, dir * step)
      : new Date(d.getFullYear(), d.getMonth() + dir, 1)))

  // Built by hand rather than by juggling Intl options: asking for year and day
  // without a month renders "2026 (day: 10)", which is what the first attempt did.
  const heading = useMemo(() => {
    if (view !== 'week') return anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    const last = addDays(from, 6)
    const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'long' })
    return from.getMonth() === last.getMonth()
      ? `${month(from)} ${from.getDate()} – ${last.getDate()}, ${last.getFullYear()}`
      : `${month(from)} ${from.getDate()} – ${month(last)} ${last.getDate()}, ${last.getFullYear()}`
  }, [view, from, anchor])

  // A CRM-booked appointment opens its record; one that came from Google has no
  // record to open, so it opens the event in Google instead of doing nothing.
  // Clicking an event opens the same drawer that booked it. Following the link to
  // the contact, or out to Google, is a choice inside that drawer rather than
  // something that happens to you for clicking the thing you wanted to change.
  const [editing, setEditing] = useState<Appointment | null>(null)
  const open = (a: Appointment) => setEditing(a)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="h1">Calendar</h1>
          <p className="subline">
            {data?.calendar.connected
              ? <>Two-way with <strong>{data.calendar.email}</strong> — book here and it appears there.</>
              : <>Not synced with Google. Connect one under <a href="/customize?tab=calendar">Customize → Calendar</a>.</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn" onClick={() => setAnchor(startOfDay(new Date()))}>Today</button>
          <button className="btn" onClick={() => go(-1)} aria-label="Previous"><Icon name="chevron_left" size={16} /></button>
          <button className="btn" onClick={() => go(1)} aria-label="Next"><Icon name="chevron_right" size={16} /></button>
          <button className="btn primary" onClick={() => setBooking(true)}>
            <Icon name="add" size={16} /> New appointment
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, margin: '18px 0 12px' }}>
        <h2 className="h2" style={{ margin: 0 }}>{heading}</h2>
        <span className="spacer" style={{ flex: 1 }} />
        <div className="tabs" style={{ marginTop: 0, borderBottom: 'none' }}>
          {(['week', 'month'] as View[]).map((v) => (
            <button key={v} className={`tab${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
              {v === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? <Loading />
        : error || !data ? <ErrorState error={error} onRetry={reload} />
        : view === 'week'
          ? <WeekGrid from={from} timed={timed} allDay={allDay} firstHour={firstHour} lastHour={lastHour} onOpen={open} />
          : <MonthGrid from={from} anchor={anchor} appts={appts} onOpen={open} />}

      {editing ? (
        <BookModal account={data?.calendar.connected ? data.calendar.email : null}
          existing={editing}
          onOpenRecord={editing.href ? () => navigate(editing.href!) : undefined}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }} />
      ) : null}

      {booking ? (
        <BookModal account={data?.calendar.connected ? data.calendar.email : null}
          onClose={() => setBooking(false)} onSaved={() => { setBooking(false); reload() }} />
      ) : null}
    </div>
  )
}

function Chip({ a, onOpen, compact }: { a: Appointment; onOpen: (a: Appointment) => void; compact?: boolean }) {
  const colour = tint(a.kind || a.title)
  const past = new Date(a.ends_at).getTime() < Date.now()
  return (
    <button
      onClick={() => onOpen(a)}
      title={`${hhmm(new Date(a.starts_at))} ${a.title}${a.contact_name ? ` · ${a.contact_name}` : ''}`}
      style={{
        display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: a.href ? 'pointer' : 'default',
        borderRadius: 6, padding: compact ? '1px 5px' : '3px 6px', marginBottom: 2,
        fontSize: compact ? 11.5 : 12, lineHeight: 1.3, overflow: 'hidden',
        whiteSpace: compact ? 'nowrap' : 'normal', textOverflow: 'ellipsis',
        opacity: past ? 0.55 : 1, ...colour,
      }}
    >
      {compact ? <><span className="mono" style={{ fontSize: 10.5 }}>{hhmm(new Date(a.starts_at))}</span> {a.title}</>
        : <><strong style={{ fontWeight: 600 }}>{a.title}</strong>
          {a.contact_name ? <div style={{ opacity: 0.8 }}>{a.contact_name}</div> : null}</>}
    </button>
  )
}

function WeekGrid({ from, timed, allDay, firstHour, lastHour, onOpen }: {
  from: Date; timed: Appointment[]; allDay: Appointment[]
  firstHour: number; lastHour: number; onOpen: (a: Appointment) => void
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(from, i))
  const hours = Array.from({ length: lastHour - firstHour }, (_, i) => firstHour + i)
  const gridHeight = hours.length * ROW_PX

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {/* Day headings */}
      <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(7, 1fr)`, borderBottom: '1px solid var(--line)' }}>
        <div />
        {days.map((d) => {
          const today = sameDay(d, new Date())
          return (
            <div key={+d} style={{ padding: '9px 8px', textAlign: 'center', borderLeft: '1px solid var(--line-faint)' }}>
              <div className="quiet" style={{ fontSize: 11, letterSpacing: 0.4 }}>{DAY_NAMES[d.getDay()]!.toUpperCase()}</div>
              <div style={{
                fontSize: 15, fontWeight: today ? 700 : 500, marginTop: 2,
                color: today ? 'var(--accent)' : 'var(--ink)',
              }}>{d.getDate()}</div>
            </div>
          )
        })}
      </div>

      {/* All-day strip, only when there is one */}
      {allDay.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(7, 1fr)`, borderBottom: '1px solid var(--line)' }}>
          <div className="quiet" style={{ fontSize: 10.5, padding: '6px 6px', textAlign: 'right' }}>all day</div>
          {days.map((d) => (
            <div key={+d} style={{ padding: 4, borderLeft: '1px solid var(--line-faint)', minHeight: 26 }}>
              {allDay.filter((a) => sameDay(new Date(a.starts_at), d))
                .map((a) => <Chip key={a.id} a={a} onOpen={onOpen} compact />)}
            </div>
          ))}
        </div>
      ) : null}

      {/* Hour grid */}
      <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(7, 1fr)`, position: 'relative' }}>
        <div>
          {hours.map((h) => (
            <div key={h} className="quiet mono" style={{ height: ROW_PX, fontSize: 10.5, textAlign: 'right', paddingRight: 7, transform: 'translateY(-6px)' }}>
              {h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}
            </div>
          ))}
        </div>

        {days.map((d) => {
          const dayStart = startOfDay(d).getTime() + firstHour * 3600_000
          const events = timed
            .filter((a) => sameDay(new Date(a.starts_at), d))
            .map((a) => ({
              appt: a,
              from: new Date(a.starts_at).getTime(),
              to: Math.max(new Date(a.ends_at).getTime(), new Date(a.starts_at).getTime() + 15 * 60_000),
            }))
          const pxPerMs = ROW_PX / 3600_000

          return (
            <div key={+d} style={{ position: 'relative', borderLeft: '1px solid var(--line-faint)', height: gridHeight }}>
              {hours.map((h) => (
                <div key={h} style={{ height: ROW_PX, borderTop: '1px solid var(--line-faint)' }} />
              ))}
              {layout(events).map(({ appt, from: s, to: e, col, of }) => (
                <div key={appt.id} style={{
                  position: 'absolute',
                  top: Math.max(0, (s - dayStart) * pxPerMs),
                  height: Math.max(16, (e - s) * pxPerMs - 2),
                  left: `calc(${(col / of) * 100}% + 2px)`,
                  width: `calc(${100 / of}% - 4px)`,
                }}>
                  <Chip a={appt} onOpen={onOpen} />
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MonthGrid({ from, anchor, appts, onOpen }: {
  from: Date; anchor: Date; appts: Appointment[]; onOpen: (a: Appointment) => void
}) {
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i))
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--line)' }}>
        {DAY_NAMES.map((n) => (
          <div key={n} className="quiet" style={{ padding: '8px 10px', fontSize: 11, letterSpacing: 0.4, textAlign: 'center' }}>
            {n.toUpperCase()}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((d) => {
          const mine = appts.filter((a) => sameDay(new Date(a.starts_at), d))
          const outside = d.getMonth() !== anchor.getMonth()
          const today = sameDay(d, new Date())
          return (
            <div key={+d} style={{
              minHeight: 96, padding: 6, borderTop: '1px solid var(--line-faint)',
              borderLeft: '1px solid var(--line-faint)',
              background: outside ? 'var(--surface-sunk)' : undefined,
            }}>
              <div style={{
                fontSize: 12, fontWeight: today ? 700 : 500, marginBottom: 4,
                color: today ? 'var(--accent)' : outside ? 'var(--ink-4)' : 'var(--ink-2)',
              }}>{d.getDate()}</div>
              {mine.slice(0, 3).map((a) => <Chip key={a.id} a={a} onOpen={onOpen} compact />)}
              {mine.length > 3 ? <div className="quiet" style={{ fontSize: 10.5, paddingLeft: 5 }}>+{mine.length - 3} more</div> : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const KINDS: AppointmentKind[] = ['consultation', 'application', 'signing', 'closing', 'other']

const pad = (n: number) => String(n).padStart(2, '0')

/** The datetime-local value for an instant, in the browser's own zone. */
function localValue(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Booking and editing are the same form: the fields, the Google note and the
 *  "continue in Google" escape hatch are identical, and only the verb differs.
 *  Opening an event in a read-only peek taught an officer to go to Google to
 *  change anything, which is the opposite of what this calendar is for. */
function BookModal({ account, existing, onOpenRecord, onClose, onSaved }: {
  account: string | null
  /** Editing this one, rather than making a new one. */
  existing?: Appointment | null
  /** Where the appointment points — the contact or the loan it was booked against. */
  onOpenRecord?: () => void
  onClose: () => void; onSaved: () => void
}) {
  const calendarEmail = account
  const now = new Date()
  now.setMinutes(now.getMinutes() > 30 ? 60 : 30, 0, 0)

  const start0 = existing ? new Date(existing.starts_at) : now
  const [title, setTitle] = useState(existing?.title ?? '')
  const [startsAt, setStartsAt] = useState(localValue(start0))
  const [minutes, setMinutes] = useState(existing?.ends_at
    ? Math.max(5, Math.round((new Date(existing.ends_at).getTime() - start0.getTime()) / 60_000))
    : 30)
  const [kind, setKind] = useState<AppointmentKind>(
    (existing?.kind as AppointmentKind | undefined) ?? 'consultation')
  const [location, setLocation] = useState(existing?.location ?? '')
  const [contact, setContact] = useState<{ id: number; name: string | null }[]>(
    existing?.contact_id ? [{ id: existing.contact_id, name: existing.contact_name ?? null }] : [])
  const [description, setDescription] = useState(existing?.description ?? '')
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const openInGoogle = () => {
    const start = startsAt ? new Date(startsAt) : null
    openExternal(googleComposeUrl({
      title: title.trim() || undefined,
      startsAt: start,
      endsAt: start ? new Date(start.getTime() + minutes * 60_000) : null,
      location: location.trim() || null,
      account: calendarEmail,
    }))
  }

  const save = async () => {
    if (!title.trim() || !startsAt || busy) return
    if (!Number.isFinite(new Date(startsAt).getTime())) return setFormError('Choose a valid start date and time.')
    setFormError('')
    setBusy(true)
    try {
      const body = {
        title: title.trim(), kind, location: location.trim() || null,
        contact_id: contact[0]?.id, description: description.trim() || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(new Date(startsAt).getTime() + minutes * 60_000).toISOString(),
      }
      const saved = existing
        ? await api.updateAppointment(existing.id, body)
        : await api.createAppointment(body)
      toast(saved.google_event_id
        ? `${existing ? 'Saved' : 'Booked'}, and on your Google Calendar.`
        : `Appointment ${existing ? 'saved' : 'booked'}.`)
      onSaved()
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
      setBusy(false)
    }
  }

  return (
    <Modal open title={existing ? 'Edit appointment' : 'New appointment'}
      subtitle={existing ? 'Change it here and Google gets the change too.'
        : 'Make time for the next conversation.'}
      variant="drawer" className="appointment-drawer"
      onClose={() => { if (!busy) onClose() }} width={560}
      footer={<><div className="appointment-save-note"><Icon name={calendarEmail ? 'sync' : 'event'} size={16} /><span>{calendarEmail ? 'Saves to CRM & Google Calendar' : 'Saves to your CRM calendar'}</span></div><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" form="book-appointment" type="submit" disabled={busy || !title.trim() || !startsAt}>{busy ? 'Booking…' : 'Book appointment'}<Icon name="arrow_forward" size={16} /></button></>}>
      <form id="book-appointment" className="appointment-form" onSubmit={e => { e.preventDefault(); void save() }}>
        <div className="appointment-intro"><span className="appointment-intro-icon"><Icon name="event_available" size={25} /></span><div><span className="panel-overline">A MOMENT TO CONNECT</span><p>{calendarEmail ? <>Booking with <strong>{calendarEmail}</strong></> : 'A new appointment on your calendar'}</p></div></div>
        <label className="appointment-title-field"><span>Appointment title</span><input className="field" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Home financing consultation" required autoFocus /></label>
        <fieldset className="appointment-section"><legend><Icon name="schedule" size={17} /> When</legend><div className="appointment-time-grid"><DateField value={startsAt} onChange={setStartsAt} label="Date & start time" withTime /><label className="fl"><span>Duration</span><select className="field" value={minutes} onChange={e => setMinutes(Number(e.target.value))}>{[15,30,45,60,90].map(m => <option key={m} value={m}>{m} minutes</option>)}</select></label></div><p className="appointment-timezone">{Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g,' ')} · Ends {startsAt && Number.isFinite(new Date(startsAt).getTime()) ? new Date(new Date(startsAt).getTime() + minutes * 60000).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'}) : '—'}</p></fieldset>
        <fieldset className="appointment-section"><legend><Icon name="person_outline" size={17} /> Who & where</legend><div className="appointment-contact"><span className="appointment-field-label">Link to a contact <small>Optional</small></span><PeoplePicker selected={contact} onChange={people => setContact(people.slice(-1))} placeholder="Search your contacts by name" /></div><div className="appointment-detail-grid"><label className="fl"><span>Appointment type</span><select className="field" value={kind} onChange={e => setKind(e.target.value as AppointmentKind)}>{KINDS.map(k => <option key={k} value={k}>{k[0]!.toUpperCase()+k.slice(1)}</option>)}</select></label><label className="fl"><span>Location or meeting link</span><input className="field" value={location} onChange={e => setLocation(e.target.value)} placeholder="Office, phone, or video link" /></label></div></fieldset>
        <label className="fl appointment-notes"><span>Notes <small>Optional</small></span><textarea className="field" value={description} onChange={e => setDescription(e.target.value)} placeholder="Anything to prepare before the meeting…" rows={3} /></label>
        {formError ? <p className="field-error" role="alert">{formError}</p> : null}
        {existing && onOpenRecord ? (
          <button className="text-action" type="button" onClick={onOpenRecord}>
            Open the record this is for <Icon name="north_east" size={14} />
          </button>
        ) : null}
        <details className="appointment-google-options"><summary><Icon name="open_in_new" size={15} /> More options in Google Calendar <Icon name="expand_more" size={16} /></summary><p>Add guests, a Meet link, or a repeating schedule in Google. The event will sync back to your calendar{contact.length ? ', but will not be linked to the contact selected here' : ''}.</p><button className="text-action" type="button" onClick={openInGoogle}>Continue in Google <Icon name="north_east" size={14} /></button></details>
      </form>
    </Modal>
  )
}
